import { ModernSelect, SelectOption } from "@components/Select";
import {
  getDefaultFormControlSx,
  getDefaultSelectSx
} from "@components/Select/ModernSelect";
import { BUTTON_PRIMARY_ICON_FONT_SIZE } from "@constants/button";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Chip,
  Divider,
  Fab,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import {
  ClientCreate,
  ClientUIMMMModelCurrencyOptions,
  ClientUIMMMModelMetricOptions,
  ClientUIMMMModelUIModulesOptions
} from "@services/api/types";
import { getTextFieldBasicSx, getTextFieldSx } from "@styles/TextField";
import { validateEmail } from "@utils/email";
import { convertFileToBase64 } from "@utils/image";
import { MuiColorInput } from "mui-color-input";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
      borderRadius: 2,
      mt: 1,
      boxShadow: "0 1px 4px #2645F5"
    }
  }
};

export interface ClientFormProps {
  mode: "create" | "update";
  client: ClientCreate;
  selectedFile: File | null;
  processing: boolean;
  setSelectedFile: (file: File | null) => void;
  onSave: (event: any) => void;
  onGoBack: () => void;
  handleFieldChange: (field: keyof ClientCreate, value: any) => void;
  handleMMMModelMetricsChange: (event: SelectChangeEvent) => void;
  handleMMMModelUIModulesChange: (event: SelectChangeEvent) => void;
}

const ClientForm = ({
  mode = "create",
  client,
  selectedFile,
  processing,
  setSelectedFile,
  onSave,
  onGoBack,
  handleFieldChange,
  handleMMMModelMetricsChange,
  handleMMMModelUIModulesChange
}: ClientFormProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const [phone, setPhone] = useState("");
  const [selectedFileBase64, setSelectedFileBase64] = useState<string>("");
  const [textFieldHeight, setTextFieldHeight] = useState<number>(56); // Default MUI TextField input height
  const emailInputRef = useRef<HTMLInputElement>(null);

  const isSmallScreen = useMemo(
    () => isMobile || isTablet,
    [isMobile, isTablet]
  );

  useEffect(() => {
    if (selectedFile) {
      convertFileToBase64(selectedFile).then((base64) => {
        setSelectedFileBase64(base64);
      });
    }
  }, [selectedFile]);

  useEffect(() => {
    if (emailInputRef.current) {
      const height = emailInputRef.current.offsetHeight;
      if (height > 0) {
        setTextFieldHeight(height);
      }
    }
  }, []);

  const handleUploadClick = (event: any) => {
    setSelectedFile(event.target.files[0]);
  };

  const mmmModelMetricOptions = useMemo(() => {
    return ClientUIMMMModelMetricOptions.map((metric) => (
      <MenuItem key={metric} value={metric}>
        {t(`newClient.mmm_model_metrics.${metric}`)}
      </MenuItem>
    ));
  }, []);

  const mmmModelUIModulesOptions = useMemo(() => {
    return ClientUIMMMModelUIModulesOptions.map((metric) => (
      <MenuItem key={metric} value={metric}>
        {t(`newClient.mmm_model_ui_modules.${metric}`)}
      </MenuItem>
    ));
  }, []);

  const currencyOptions = useMemo(() => {
    return ClientUIMMMModelCurrencyOptions.reduce((acc, currency) => {
      acc.push({
        value: currency.symbol,
        label: `${currency.symbol} - ${currency.label}`
      });
      return acc;
    }, [] as SelectOption[]);
  }, [t]);

  const textFieldSx = useMemo(() => getTextFieldSx(theme), [theme]);
  const textFieldBasicSx = useMemo(() => getTextFieldBasicSx(theme), [theme]);

  const isUpdateMode = useMemo(() => mode === "update", [mode]);

  return (
    <Box
      component="form"
      sx={{
        "& .MuiTextField-root": { p: 1 },
        width: "100%",
        height: "100%"
      }}
      noValidate
      autoComplete="off"
      className="p-8"
      onSubmit={onSave}
    >
      <Grid container spacing={2} justifyContent="center">
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center" }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newClient.general_information")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          <TextField
            id="new-client-name"
            key="new-client-name"
            label={t("newClient.name")}
            color="info"
            value={client.name}
            onChange={(event) => handleFieldChange("name", event.target.value)}
            required
            fullWidth
            disabled={isUpdateMode}
            sx={isUpdateMode ? { ...textFieldBasicSx } : { ...textFieldSx }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          <TextField
            id="new-client-label"
            key="new-client-label"
            label={t("newClient.label")}
            color="info"
            value={client.label}
            onChange={(event) => handleFieldChange("label", event.target.value)}
            required
            fullWidth
            sx={{ ...textFieldSx }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          <TextField
            inputRef={emailInputRef}
            id="new-client-email"
            key="new-client-email"
            label={t("newClient.email")}
            color="info"
            value={client.email}
            onChange={(event) => handleFieldChange("email", event.target.value)}
            error={!validateEmail(client.email)}
            helperText={
              !validateEmail(client.email) ? t("newClient.emailError") : ""
            }
            sx={{ ...textFieldSx }}
            required
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              "& .react-tel-input .form-control": {
                transition: "border-color 0.2s ease-in-out",
                borderRadius: "8px",
                backgroundColor: theme.palette.background.paper
              },
              "& .react-tel-input .form-control:hover": {
                borderColor: theme.palette.primary.main
              },
              "& .react-tel-input .form-control:focus": {
                borderColor: theme.palette.primary.main,
                borderWidth: "2px",
                boxShadow: "none"
              },
              "& .react-tel-input .flag-dropdown": {
                transition: "border-color 0.2s ease-in-out",
                borderRadius: "8px 0 0 8px",
                backgroundColor: theme.palette.background.paper
              },
              "& .react-tel-input .flag-dropdown:hover": {
                borderColor: theme.palette.primary.main
              },
              "& .react-tel-input .flag-dropdown:focus": {
                borderColor: theme.palette.primary.main,
                borderWidth: "2px",
                boxShadow: "none"
              }
            }}
          >
            <PhoneInput
              value={phone}
              enableAreaCodes={false}
              onChange={(phone) => {
                setPhone(phone);
                handleFieldChange("phone", phone);
              }}
              inputProps={{
                required: true,
                autoFocus: false
              }}
              country="es"
              specialLabel={t("newClient.phone")}
              containerStyle={{
                marginLeft: 8,
                marginTop: 8,
                marginBottom: 8,
                paddingRight: 16,
                width: "100%",
                height: textFieldHeight,
                fontFamily: "Manrope",
                fontWeight: 400,
                fontSize: "1rem"
              }}
              inputStyle={{
                width: "100%",
                height: textFieldHeight
              }}
            />
          </Box>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center", marginTop: 4 }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newClient.mmm_model_information")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          <TextField
            id="new-client-kpi_label"
            key="new-client-kpi_label"
            label={t("newClient.kpi_label")}
            color="info"
            value={client.kpi_label}
            onChange={(event) =>
              handleFieldChange("kpi_label", event.target.value)
            }
            disabled={isUpdateMode}
            sx={isUpdateMode ? { ...textFieldBasicSx } : { ...textFieldSx }}
            required
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6 }} sx={{ justifyContent: "center" }}>
          <FormControl
            fullWidth
            sx={{
              ...getDefaultFormControlSx("light", undefined, false, theme)
            }}
          >
            <InputLabel id="mmm-model-metrics-select-helper-label">{`${t("newClient.mmm_model_config.metrics")} *`}</InputLabel>
            <Select
              labelId="mmm-model-metrics-select-helper-label"
              id="mmm-model-metrics-select-helper"
              value={client.mmm_model_config.metrics}
              label={`${t("newClient.mmm_model_config.metrics")} *`}
              // @ts-expect-error TODO: fix this
              onChange={handleMMMModelMetricsChange}
              multiple
              required
              input={<OutlinedInput id="select-multiple-chip" label="Chip" />}
              renderValue={(selected) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      width: "100%"
                    }}
                  >
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={t(
                          `newClient.mmm_model_metrics.${value}${isSmallScreen ? "_short" : ""}`
                        )}
                        sx={{
                          fontSize: isDesktop ? "1.0rem" : "0.5rem"
                        }}
                      />
                    ))}
                  </Box>
                );
              }}
              MenuProps={MenuProps}
              sx={{
                ...getDefaultSelectSx("light", "medium", theme),
                height: textFieldHeight
              }}
              className="mt-2 mr-2 ml-2"
            >
              {mmmModelMetricOptions}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{ xs: 12, sm: 12, md: 12 }}
          sx={{ justifyContent: "center", marginTop: 4 }}
        >
          <Typography gutterBottom variant="body2" sx={{ fontWeight: "bold" }}>
            {t("newClient.ui_information")}
          </Typography>
          <Divider />
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <FormControl
            fullWidth
            sx={{
              ...getDefaultFormControlSx("light", undefined, false, theme),
              marginTop: 5
            }}
          >
            <InputLabel id="mmm-model-ui-config-select-helper-label">{`${t("newClient.mmm_model_config.ui_modules")} *`}</InputLabel>
            <Select
              labelId="mmm-model-ui-config-select-helper-label"
              id="mmm-model-ui-config-select-helper"
              value={client.ui_modules}
              label={`${t("newClient.mmm_model_config.ui_modules")} *`}
              // @ts-expect-error TODO: fix this
              onChange={handleMMMModelUIModulesChange}
              multiple
              required
              input={
                <OutlinedInput
                  id="select-ui-config-multiple-chip"
                  label="Chip"
                />
              }
              renderValue={(selected) => {
                return (
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      width: "100%"
                    }}
                  >
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={t(`newClient.mmm_model_ui_modules.${value}`)}
                        sx={{
                          fontSize: isDesktop ? "1.0rem" : "0.5rem"
                        }}
                      />
                    ))}
                  </Box>
                );
              }}
              MenuProps={MenuProps}
              sx={{
                ...getDefaultSelectSx("light", "medium", theme),
                height: textFieldHeight
              }}
              className="mt-2 mr-2 ml-2"
            >
              {mmmModelUIModulesOptions}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <label htmlFor="mmm-model-currency-helper">
            <Box className="flex flex-col items-center mt-4">
              <Typography gutterBottom variant="body2" className="pb-2">
                {t("newClient.mmm_model_config.currency")}
                {" *"}
              </Typography>

              <ModernSelect
                options={currencyOptions}
                value={client.currency}
                onChange={(event) =>
                  handleFieldChange("currency", event.target.value)
                }
                valueKey="id"
                labelKey="name"
                showIndicatorDot={true}
                colorTheme="light"
                id="mmm-model-currency-helper"
                disabled={processing}
                sx={{
                  marginBottom: 2,
                  marginLeft: 1,
                  marginRight: 1,
                  height: textFieldHeight
                }}
              />
            </Box>
          </label>
        </Grid>
        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <Box className="flex flex-col items-center">
            {selectedFile && selectedFileBase64.length > 0 && (
              <Box className="flex flex-row items-center">
                <img
                  src={`data:image/png;base64,${selectedFileBase64}`}
                  alt="selected file"
                  className="w-full h-full object-contain"
                  style={{ maxWidth: "12rem", maxHeight: "5rem" }}
                />
                <IconButton color="error" onClick={() => setSelectedFile(null)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            <input
              accept="image/*"
              className="hidden"
              id="contained-button-file"
              type="file"
              onChange={handleUploadClick}
            />
            <label htmlFor="contained-button-file">
              <Box className="flex flex-col items-center mt-4">
                <Typography gutterBottom variant="body2" className="pb-2">
                  {t("newClient.logo_path")}
                  {" *"}
                </Typography>
                <Fab component="span">
                  <AddPhotoAlternateIcon color="info" />
                </Fab>
              </Box>
            </label>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 3, md: 3 }} sx={{ justifyContent: "center" }}>
          <label htmlFor="base-color-input">
            <Box className="flex flex-col items-center mt-4">
              <Typography gutterBottom variant="body2">
                {t("newClient.base_color")}
                {" *"}
              </Typography>
              <MuiColorInput
                format="hex"
                id="base-color-input"
                value={client.base_color}
                onChange={(newColor) =>
                  handleFieldChange("base_color", newColor)
                }
                sx={{ ...getTextFieldSx(theme) }}
                className="w-full"
              />
            </Box>
          </label>
        </Grid>
      </Grid>
      <Box className="flex justify-end p-8 gap-4">
        <Tooltip title={t("common.discard")}>
          <IconButton color="error" onClick={onGoBack} disabled={processing}>
            <CancelIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("common.save")}>
          <IconButton color="success" type="submit" disabled={processing}>
            <SaveIcon sx={{ fontSize: BUTTON_PRIMARY_ICON_FONT_SIZE }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ClientForm;
